// path: src/Home.js
import React, { useEffect, useState } from "react";
import Login from "./Login";
import "./App.css";
import DifficultyDialog from "./Components/DifficultyDialog";
import TutorialDialog from "./Components/TutorialDialog";
import {
    CHALLENGING_START_LEVEL,
    HARD_START_LEVEL,
    isShyCorgisUnlocked,
    isTwistedModeUnlocked,
    scoreAtStartOfLevel,
} from "./utils/progression";

const TUTORIAL_SEEN_KEY = "mmu_tutorial_seen_v1";
const ADVANCED_TUTORIAL_PROMPT_SEEN_KEY =
    "mmu_advanced_tutorial_prompt_seen_v1";

const Home = ({
    onPlay,
    onProfile,
    onLoginSuccess,
    onLogout,
    isAuthenticated,
    user,
}) => {
    const [difficultyOpen, setDifficultyOpen] = useState(false);
    const [tutorialOpen, setTutorialOpen] = useState(false);
    const [tutorialType, setTutorialType] = useState("menu");
    const [openDifficultyAfterTutorial, setOpenDifficultyAfterTutorial] =
        useState(false);

    const hasSeenTutorial = () => {
        try {
            return localStorage.getItem(TUTORIAL_SEEN_KEY) === "true";
        } catch {
            return false;
        }
    };

    const markTutorialSeen = () => {
        try {
            localStorage.setItem(TUTORIAL_SEEN_KEY, "true");
        } catch {
            // ignore localStorage failures
        }
    };

    const hasSeenAdvancedTutorialPrompt = () => {
        try {
            return (
                localStorage.getItem(ADVANCED_TUTORIAL_PROMPT_SEEN_KEY) === "true"
            );
        } catch {
            return false;
        }
    };

    const markAdvancedTutorialPromptSeen = () => {
        try {
            localStorage.setItem(ADVANCED_TUTORIAL_PROMPT_SEEN_KEY, "true");
        } catch {
            // ignore localStorage failures
        }
    };

    const advancedTutorialUnlocked = () => {
        return isTwistedModeUnlocked() || isShyCorgisUnlocked();
    };

    useEffect(() => {
        if (tutorialOpen || difficultyOpen) {
            return;
        }

        if (!advancedTutorialUnlocked()) {
            return;
        }

        if (hasSeenAdvancedTutorialPrompt()) {
            return;
        }

        markAdvancedTutorialPromptSeen();
        setTutorialType("advancedPrompt");
        setOpenDifficultyAfterTutorial(false);
        setTutorialOpen(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tutorialOpen, difficultyOpen]);

    const handlePlayClick = () => {
        if (hasSeenTutorial()) {
            setDifficultyOpen(true);
            return;
        }

        setTutorialType("basic");
        setOpenDifficultyAfterTutorial(true);
        setTutorialOpen(true);
    };

    const handleHowToPlayClick = () => {
        setTutorialType("menu");
        setOpenDifficultyAfterTutorial(false);
        setTutorialOpen(true);
    };

    const handleTutorialClose = () => {
        setTutorialOpen(false);
        setOpenDifficultyAfterTutorial(false);
        setTutorialType("menu");
    };

    const handleTutorialComplete = () => {
        markTutorialSeen();
        setTutorialOpen(false);

        if (openDifficultyAfterTutorial) {
            setDifficultyOpen(true);
        }

        setOpenDifficultyAfterTutorial(false);
        setTutorialType("menu");
    };

    const handlePickDifficulty = (mode, options = {}) => {
        let startLevel = 1;

        if (mode === "challenging") {
            startLevel = CHALLENGING_START_LEVEL;
        }

        if (mode === "hard") {
            startLevel = HARD_START_LEVEL;
        }

        if (mode === "blitz") {
            startLevel = 1;
        }

        const startScore = scoreAtStartOfLevel(startLevel);

        onPlay({
            startLevel,
            startScore,
            twistedMode: Boolean(options.twistedMode),
            shyCorgisMode: Boolean(options.shyCorgisMode),
            hardcoreMode: mode === "blitz" ? true : Boolean(options.hardcoreMode),
            blitzMode: mode === "blitz",
        });

        setDifficultyOpen(false);
    };

    return (
        <div className="homeScreen">
            <div className="homeCard">
                <img
                    src="images/home-hero.png"
                    alt="Memory Mix-Up"
                    className="homeImage"
                />

                <h1 className="homeTitle impactFont">Memory Mix-Up</h1>

                <div className="homeButtons">
                    <button className="homePrimaryButton" onClick={handlePlayClick}>
                        Play
                    </button>

                    <button
                        className="homePrimaryButton homeSecondaryButton"
                        onClick={handleHowToPlayClick}
                    >
                        How to Play
                    </button>

                    <button className="homePrimaryButton" onClick={onProfile}>
                        Profile
                    </button>

                    {!isAuthenticated ? (
                        <Login onSuccess={onLoginSuccess} />
                    ) : (
                        <button className="homePrimaryButton" onClick={onLogout}>
                            Logout
                        </button>
                    )}
                </div>

                <div className="homeHint">
                    {!isAuthenticated
                        ? "Login is optional — guest play is supported."
                        : `Logged in as ${user?.email}`}
                </div>

                <TutorialDialog
                    open={tutorialOpen}
                    tutorialType={tutorialType}
                    onClose={handleTutorialClose}
                    onComplete={handleTutorialComplete}
                />

                <DifficultyDialog
                    open={difficultyOpen}
                    onClose={() => setDifficultyOpen(false)}
                    onPick={handlePickDifficulty}
                />
            </div>
        </div>
    );
};

export default Home;